/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {SpinnerService} from '../../shared/spinner/spinner.service';
import {SpinnerContextTypeEnum} from '../../shared/spinner/spinner-context-type.enum';
import {BundlesApi} from '../api/bundles.api';
import {BundleTypeEnum} from './data/bundle-type.enum';
import {TabsModel} from '../../shared/tabs/models/tabs.model';
import {
    combineLatest,
    forkJoin,
    Observable,
    ReplaySubject,
    Subscription
} from 'rxjs';
import {CustomerBaseContract} from '../api/contracts/customer/customer-base.contract';
import {SubTabsModel} from '../../shared/tabs/models/sub-tabs.model';
import {PeriodSearchCriteriaModel} from '../models/period/period-search-criteria/period-search-criteria.model';
import {StatefulComponent} from '../../shared/stateful/stateful.component';
import {BundlesMainPageState} from './models/bundles-main-page.state';
import {PeriodsTableComponent} from '../periods-table/periods-table.component';
import {
    finalize
} from 'rxjs/operators';
import {TabsState} from '../../shared/tabs/tabs.state';
import {SelectedTabsService} from '../services/selected-tabs.service';
import {PeriodViewTabsEnum} from './data/period-view-tabs.enum';
import {periodViewTabsConfiguration} from './configuration/period-view-tabs-configuration';
import {PeriodQueryModel} from '../models/period/period-query/period-query.model';
import {PeriodResultContract} from '../api/contracts/period/period-result.contract';
import {PeriodsTableSearchCriteriaService} from '../periods-table/services/periods-table-search-criteria.service';
import {SegmentedButtonComponent} from '../../shared/segemented-button/segmented-button.component';
import {PeriodResultMapper} from '../models/period/period-result/period-result.mapper';
import {BundlesConfigService} from '../services/bundles-config.service';
import {BundleConfigContract} from '../api/contracts/bundle-config/bundle-config.contract';
import {PeriodDetailModel} from '../models/period/period-detail/period-detail.model';
import {PeriodsViewsService} from '../services/periods-views/periods-views.service';
import {QueryChipModel} from '../../shared/global-search-criteria/query-chips-area/models/query-chip.model';
import {GlobalSearchCriteriaApplyMapper} from './mappers/global-search-criteria-apply.mapper';
import {bundlesGlobalSearchCriteriaConfiguration} from './configuration/bundles-global-criteria-search-configuration';
import {bundlesAutoCloseConfiguration} from './configuration/bundles-auto-close-configuration';
import {SearchCriteriaConfiguration} from '../../shared/global-search-criteria/configuration/search-criteria-configuration';
import {CustomerBundleTypeBaseContract} from '../api/contracts/bundle/customer-bundle-type-base.contract';
import {SearchCriteriaInputTypeEnum} from '../../shared/global-search-criteria/configuration/search-criteria-input-type.enum';
import {BundleDynamicCriteriaEnum} from './data/bundle-dynamic-criteria.enum';
import {CriterionContextCodeEnum} from './configuration/criterion-context-code.enum';
import {fastDeepCopy} from '../../shared/utils/deep-copy';
import {Permission} from '../../shared/utils/permission.enum';
import {StickyHeaderService} from '../../shared/sticky-header/sticky-header.service';
import {UserService} from '../../shared/services/user.service';
import {BundlingSettingsDetailContract} from '../api/contracts/bundle-settings/bundling-settings-detail.contract';
import {BundlingSettingsDetailMapper} from './mappers/bundling-settings-detail/bundling-settings-detail.mapper';
import {StickyHeaderSimpleButtonComponent} from '../../shared/sticky-header/html-elements/sticky-header-simple-button.component';
import {Router} from '@angular/router';
import {BundlingSettingsTabsMapper} from './mappers/bundling-settings-tabs/bundling-settings-tabs.mapper';
import {BundlingSettingsCustomerBundlesActiveTabsModel} from './models/bundling-settings-tabs/bundling-settings-customer-bundles-active-tabs.model';
import {PeriodicityBaseContract} from '../api/contracts/periodicity-base.contract';
import {BundleCriterionSpecBaseContract} from '../api/contracts/bundle/bundle-criterion-spec-base.contract';
import {BundlePositionCriterionSpecBaseContract} from '../api/contracts/bundle/bundle-position-criterion-spec-base.contract';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {BundleSettingsFormService} from '../services/bundle-settings-form.service';
import {ReferenceDataModel} from '../models/reference-data/reference-data.model';
import {GlobalSearchCriteriaModel} from '../../shared/global-search-criteria/models/global-search-criteria.model';
import {CriterionCodeEnum} from './configuration/criterion-code.enum';
import {ConfigPropertyApi} from '../../shared/api/config-property/config-property.api';
import {environment} from '../../../environments/environment';
import {SwitchButtonModel} from '../../shared/switch-button/models/switch-button.model';
import {datePipe} from '../util/bundle-data-pipes';
import {UserBaseModel} from '../models/user-base/user-base.model';
import {ToastWrapperService} from '../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {CurrencyBaseMapper} from '../models/currency/currency-base.mapper';
import {CustomerBundleTypeConfigMapper} from '../mappers/bundle-config/customer-bundle-type-config.mapper';

@Component({
    selector: 'app-bundles-main-page',
    templateUrl: './bundles-main-page.component.html',
    styleUrls: ['./bundles-main-page.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BundlesMainPageComponent extends StatefulComponent<BundlesMainPageState> implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild('periodsTableTemplate') periodsTableTemplate: TemplateRef<PeriodsTableComponent>;
    @ViewChild('changeViewTabs') changeViewTabs: SegmentedButtonComponent<PeriodViewTabsEnum>;

    @Output() resetGlobalSearchCriteria = new EventEmitter<void>();
    public bundlesAutoCloseConfiguration = bundlesAutoCloseConfiguration;
    public tabsData = new Map<string, TabsModel<PeriodsTableComponent>>();
    public templateMap: Map<BundleTypeEnum, TemplateRef<PeriodsTableComponent>>;
    private subscriptions = new Subscription();
    public periodViewTabs = PeriodViewTabsEnum;
    // Local Loading Key
    private readonly LOCAL_LOADING_INSTANCE_KEY = 'TAB_CONTENT_LOADING';
    public spinnerObservable = this.spinnerService.onLocalChange(this.LOCAL_LOADING_INSTANCE_KEY);
    private customerBundleTypes: CustomerBaseContract[];
    // Bundles settings header button
    private readonly bundlingSettingsAction: StickyHeaderSimpleButtonComponent = {
        text: 'Bundles settings',
        icon: 'iwp-icon-gen_settings',
        click: (): void => {
            this.getBundlingSettingsConfiguration();
        },
        id: 'sticky-header-bundle-settings-button',
        class: 'btn-primary sticky-header-bundle-settings-button',
        dataCy: 'sticky-header-bundle-settings-button',
        tooltip: 'Bundles settings'
    };
    private customerBundleTypesLoadedSubject = new ReplaySubject<void>();
    private bundleTypesLoadedSubject = new ReplaySubject<void>();
    private readonly bundlesMainPageDefaultWidth = 'auto';
    private readonly autoCloseBundles = 'autoCloseBundles';
    // Guards Variables
    public MODAL_TITLE = 'Bundling settings changes';
    public isExited = false;

    constructor(public spinnerService: SpinnerService,
                public bundleService: BundlesApi,
                public search: PeriodsTableSearchCriteriaService,
                private bundlesConfigService: BundlesConfigService,
                private cdr: ChangeDetectorRef,
                private selectedTabsService: SelectedTabsService,
                private periodsViewsService: PeriodsViewsService,
                private stickyHeaderService: StickyHeaderService,
                private router: Router,
                private modalService: NgbModal,
                private userService: UserService,
                private bundleSettingsFormService: BundleSettingsFormService,
                private configProperty: ConfigPropertyApi,
                private toast: ToastWrapperService) {
        super();
        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, null);
    }

    ngOnInit(): void {
        if (this.userService.hasPermissions([
            Permission.ADMIN_BUNDLES_SETTINGS_VIEW,
            Permission.ADMIN_BUNDLES_SETTINGS_MANAGE
        ], 'any')) {
            this.getAllBundleTypes();
            this.subscriptions.add(combineLatest([this.customerBundleTypesLoadedSubject, this.bundleTypesLoadedSubject])
                    .subscribe(() => {
                        this.stickyHeaderService.clearActions();
                        this.stickyHeaderService.registerSubmitAction(this.bundlingSettingsAction);
                    }));
        }

        if (this.userService.hasPermissions([
            Permission.ADMIN_BUNDLES_SETTINGS_MANAGE
        ], 'any')) {
            this.storeState({bundlingSettingsEditMode: true});
        }

        this.storeState({
            listTabOptions: [...periodViewTabsConfiguration.tabs],
            selectedTab: periodViewTabsConfiguration.defaultTab,
            globalSearchAppliedCriteria: [],
            bundlesGlobalDynamicSearchConfiguration: null,
            initialBundlesGlobalSearchCriteriaConfiguration: bundlesGlobalSearchCriteriaConfiguration,
            bundlesGlobalSearchModel: this.getGlobalSearchCriteriaModel(),
            bundlingSettingsOpen: false
        });
        this.autoCloseBundlesPermissions();
        this.periodsViewsService.setGlobalSearchCriteriaStatus(false);
        this.retrieveConfig();
    }

    ngAfterViewInit(): void {
        this.templateMap = new Map([
            [BundleTypeEnum.PARTS_BUNDLING, this.periodsTableTemplate],
            [BundleTypeEnum.TE_MINUTES_BUNDLING, this.periodsTableTemplate],
            [BundleTypeEnum.UNKNOWN, this.periodsTableTemplate]
        ]);
        this.getCustomerBundleTypes();
        this.cdr.detectChanges();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.stickyHeaderService.clearActions();
    }

    get bundlingSettingsActiveTabs(): BundlingSettingsCustomerBundlesActiveTabsModel {
        return {
            tabActiveCustomerCode: this.selectedTabsService.customerCode,
            tabsActiveBundleTypeCode: this.selectedTabsService.bundleType
        };
    }

    get showBundlingSettings(): boolean {
        return this.state.bundlingSettingsOpen && this.state.widthBundlesMainPage !== this.bundlesMainPageDefaultWidth;
    }

    get periodsList(): PeriodDetailModel[] {
        return this.periodsViewsService.getPeriodsList();
    }

    get getAutoCloseSwitchModel(): SwitchButtonModel {
        return this.state.autoCloseBundlesSwitchButton;
    }

    get getAutoCloseLastChangedBy(): UserBaseModel {
        return this.state.autoCloseBundlesLastChangedBy;
    }

    get getAutoCloseLastChangedOn(): string {
        return datePipe({date: this.state.autoCloseBundlesLastChangedOn, timezone: this.bundlesConfigService.timezone});
    }

    changePage = (pageNumber: number): void => {
        this.search.pageNumber = pageNumber;

        // Inform the child components that the page was changed in order to internally refresh the table results
        this.search.changePageEvent.emit();

        this.retrieveTableData(false);
    };

    hasPagination(): boolean {
        return this.search.totalResults > this.search.pageSize;
    }

    setSelectedTabs(selectedTabs: TabsState<string, BundleTypeEnum>): void {
        if (selectedTabs.activeTab !== this.state.searchCriteria.customerCode
                || selectedTabs.activeSubTab !== this.state.searchCriteria.bundleTypeCode) {
            // @ts-ignore
            const subTabs: Map<string, TabsModel<PeriodsTableComponent>> = this.tabsData.get(selectedTabs.activeTab).subTabs;
            this.storeState({
                searchCriteria: {
                    customerCode: selectedTabs.activeTab,
                    bundleTypeCode: selectedTabs.activeSubTab,
                    customerAcronym: selectedTabs.activeTab
                },
                // Default the selected period view tab
                selectedTab: periodViewTabsConfiguration.defaultTab
            });
            this.selectedTabsService.bundleType = <BundleTypeEnum> this.state.searchCriteria.bundleTypeCode;
            this.selectedTabsService.bundleTypeAcronym = subTabs.get(selectedTabs.activeSubTab)?.title;
            this.selectedTabsService.customerCode = this.state.searchCriteria.customerCode;
            this.selectedTabsService.customerAcronym = this.tabsData.get(selectedTabs.activeTab).title;

            // Reset view tabs with the default selection
            this.changeViewTabs?.resetSelectedButtons();
            this.periodsViewsService.setCurrentView(periodViewTabsConfiguration.defaultTab);

            // Reset page filter
            this.search.clear();

            this.resetCustomerBundleTypesSearchCriteria();
            this.mapCustomerBundleTypesSearchCriteria(this.customerBundleTypes);

            this.resetGlobalSearchCriteriaActions();

            this.retrieveAutomaticBundlesClosureConfig();

            this.retrieveTableData(false);
        }
    }

    changePeriodViewTab(newTab: PeriodViewTabsEnum): void {
        if (newTab !== this.state.selectedTab) {
            this.storeState({selectedTab: newTab});
            this.state.listTabOptions.map((item) => item.isSelected = false);
            this.state.listTabOptions.find((item) => item.value === newTab).isSelected = true;
            this.periodsViewsService.setCurrentView(newTab);
        }
    }

    updatePeriodData(periodDetail: PeriodDetailModel): void {
        this.periodsViewsService.updatePeriodListWithPeriodDetail(periodDetail);
    }

    updateTableData(): void {
        this.retrieveTableData(false);
    }

    globalSearchCriteriaHandler(criteriaFiltersInQuery: QueryChipModel[]): void {
        this.periodsViewsService.updateTableLinesByGlobalSearchButton = true;
        const isGlobalSearchCriteriaApplied = criteriaFiltersInQuery != null;
        this.storeState({
            globalSearchAppliedCriteria: isGlobalSearchCriteriaApplied ? criteriaFiltersInQuery : []
        });
        this.periodsViewsService.setGlobalSearchCriteriaStatus(isGlobalSearchCriteriaApplied);
        // Reset page filter
        if (criteriaFiltersInQuery === null) {
            this.changePeriodViewTab(PeriodViewTabsEnum.PERIODS);
        }

        this.search.clear();
        this.retrieveTableData(false, true);
    }

    closeBundlesSettings(): void {
        this.storeState({bundlingSettingsOpen: false, widthBundlesMainPage: this.bundlesMainPageDefaultWidth});
        this.bundleSettingsFormService.setBundlingSettingsOpen(this.state.bundlingSettingsOpen);
    }

    private getSelectBundleTypeTab(): BundleTypeEnum {
        return this.selectedTabsService.bundleType ?? <BundleTypeEnum> this.state.bundlingSettingsTabs?.tabsBundleTypesCodes[0]?.tabCode;
    }

    private getBundlingSettingsConfiguration(): void {
        const widthRemoveSettingsAreaAddMarginPage = 'calc(100% - 352px + 33px)';
        this.storeState({
            bundlingSettingsOpen: !this.state.bundlingSettingsOpen,
            widthBundlesMainPage: this.bundlesMainPageDefaultWidth
        });
        this.bundleSettingsFormService.setBundlingSettingsOpen(this.state.bundlingSettingsOpen);
        if (this.state.bundlingSettingsOpen) {
            this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
            if (this.state.bundlingSettingsEditMode) {
                this.getBundlingSettingsCreateData();
            }
            this.selectedTabsService.bundleType = this.getSelectBundleTypeTab();
            this.subscriptions.add(
                    this.bundleService.getBundlesSettings(this.selectedTabsService.customerCode, this.selectedTabsService.bundleType)
                            .pipe(finalize(() => {
                                this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                            }))
                            .subscribe((bundlingSettingsDetail: BundlingSettingsDetailContract) => {
                                this.storeState({
                                    widthBundlesMainPage: widthRemoveSettingsAreaAddMarginPage,
                                    bundlingSettingsFormData: BundlingSettingsDetailMapper
                                            .fromBundlingSettingsDetailContractToBundlingSettingsDetailModel(bundlingSettingsDetail)
                                });
                            }));

        }
    }

    private getBundlingSettingsCreateData(): void {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        forkJoin([
            this.bundleService.getActivePeriodicitiesBaseInfo(),
            this.bundleService.getActiveBundleCriterionSpecs(),
            this.bundleService.getActiveBundlePositionCriterionSpecs()
        ])
                .subscribe((([periodicities, bundleCriterionSpecs, bundlePositionCriterionSpecs]: [
                    PeriodicityBaseContract[],
                    BundleCriterionSpecBaseContract[],
                    BundlePositionCriterionSpecBaseContract[]
                ]): void => {
                    this.state.bundlingSettingsDropdowns = {
                        periodicities: periodicities,
                        bundleCriterionSpecs: bundleCriterionSpecs,
                        bundlePositionCriterionSpecs: bundlePositionCriterionSpecs
                    };
                }))
                .add(() => {
                    this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                });
    }

    private getAllBundleTypes(): void {
        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, this.getAllBundleTypes);
        this.subscriptions.add(
                this.bundleService.getAllBundleTypes()
                        .pipe(finalize(() => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.getAllBundleTypes);
                        }))
                        .subscribe((bundleTypes) => {
                            this.storeState({
                                bundlingSettingsTabs: {
                                    ...this.state.bundlingSettingsTabs,
                                    tabsBundleTypesCodes: BundlingSettingsTabsMapper
                                            .fromBundleTypeBaseContractArrayToBundlingSettingsTabModelArray(bundleTypes)
                                }
                            });
                            this.bundleTypesLoadedSubject.next();
                        }));
    }

    private getCustomerBundleTypes(): void {
        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, this.getCustomerBundleTypes);
        this.subscriptions.add(
                this.bundleService.getAllCustomersDetail()
                        .pipe(finalize(() => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.getCustomerBundleTypes);
                        }))
                        .subscribe((bundling: CustomerBaseContract[]) => {
                            this.customerBundleTypes = bundling;
                            this.mapCustomerBundleTypes(bundling);
                            this.mapCustomerBundleTypesSearchCriteria(bundling);
                            this.storeState({
                                bundlingSettingsTabs: {
                                    ...this.state.bundlingSettingsTabs,
                                    tabsCustomerCodes: BundlingSettingsTabsMapper
                                            .fromCustomerBaseContractArrayToBundlingSettingsTabModelArray(bundling)
                                }
                            });
                            // Retrieve table data now that we have the search criteria
                            this.retrieveTableData(true);
                            this.retrieveAutomaticBundlesClosureConfig();
                            this.customerBundleTypesLoadedSubject.next();
                        }));
    }

    private mapCustomerBundleTypes(bundling: CustomerBaseContract[]): void {
        bundling.forEach((bundlingValue, index) => {
            const isActive = index === 0;
            const bundlingItem = {
                title: bundlingValue.shortDescription,
                isActive: isActive,
                subTabs: this.mapBundleTypes(bundlingValue, isActive)
            };
            if (isActive) {
                this.storeState({searchCriteria: {...this.state.searchCriteria, customerCode: bundlingValue.code}});
                this.selectedTabsService.customerCode = this.state.searchCriteria.customerCode;
                this.selectedTabsService.customerAcronym = bundlingItem.title;
            }
            this.tabsData.set(bundlingValue.code, {...bundlingItem});
        });
    }

    private mapBundleTypes(bundlingValue: CustomerBaseContract, customerIsActive: boolean): any {
        const subTabs = new Map<BundleTypeEnum, SubTabsModel<any>>();
        if (bundlingValue.customerBundleTypes != null) {
            bundlingValue.customerBundleTypes.forEach((value, index) => {
                const isActive = index === 0;
                const bundlingTypeItem = {
                    title: value.bundleType.name,
                    isActive: isActive,
                    contentTemplate: this.mapBundleTemplates(value.bundleType.code) ?? this.periodsTableTemplate
                };
                if (isActive && customerIsActive) {
                    this.storeState({
                        searchCriteria: {
                            ...this.state.searchCriteria,
                            bundleTypeCode: value.bundleType.code
                        }
                    });
                    this.selectedTabsService.bundleType = <BundleTypeEnum> this.state.searchCriteria.bundleTypeCode;
                    this.selectedTabsService.bundleTypeAcronym = value.bundleType.name;
                    this.selectedTabsService.customerCode = this.state.searchCriteria.customerCode;
                    this.selectedTabsService.customerAcronym = this.state.searchCriteria.customerAcronym;
                }
                subTabs.set(<BundleTypeEnum>value.bundleType.code, {...bundlingTypeItem});
            });
            return subTabs;
        }
    }

    private mapBundleTemplates(value: string): TemplateRef<PeriodsTableComponent> {
        return this.templateMap.get(<BundleTypeEnum>value);
    }

    private retrieveConfig(): void {
        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, this.retrieveConfig);
        this.bundleService.getBundlesConfig()
                .pipe(finalize(() => {
                    this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.retrieveConfig);
                }))
                .subscribe((config: BundleConfigContract) => {
                    this.bundlesConfigService.timezone = config.timeZone;
                    this.bundlesConfigService.currency = CurrencyBaseMapper.fromCurrencyBaseContract(config.defaultCurrency);
                    this.bundlesConfigService.customerBundleTypeConfigs =
                            CustomerBundleTypeConfigMapper.fromCustomerBundleTypeConfigContractArray(config.customerBundleTypeConfigs);
                });
    };

    /**
     * Retrieves the period list data from the BE service
     */
    private retrieveTableData(useGlobalSpinner: boolean, shouldRedirectToSelectTab?: boolean): void {
        useGlobalSpinner ? this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, BundlesMainPageComponent)
                : this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);

        let periodSearchCriteria: PeriodSearchCriteriaModel = {
            customerCode: this.state.searchCriteria?.customerCode,
            bundleTypeCode: this.state.searchCriteria?.bundleTypeCode
        };
        periodSearchCriteria = GlobalSearchCriteriaApplyMapper
                .fromQueryChipModelArrayToPeriodSearchCriteriaModel(this.state.globalSearchAppliedCriteria, periodSearchCriteria);

        const query: PeriodQueryModel = this.search.getPeriodsListQuery(periodSearchCriteria);

        this.subscriptions.add(
                this.bundleService.getPeriods(query)
                        .pipe(finalize(() => {
                            useGlobalSpinner ? this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, BundlesMainPageComponent)
                                    : this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                            if (shouldRedirectToSelectTab) {
                                this.redirectToSelectTab(this.state.globalSearchAppliedCriteria);
                            }
                        }))
                        .subscribe((result: PeriodResultContract) => {

                            if (((result?.periodsPage?.length ?? 0) === 0)
                                    && result?.totalResults > 0
                                    && this.search.pageNumber > 1) {
                                this.changePage(this.search.pageNumber - 1);
                            } else {
                                this.search.totalResults = result?.totalResults;
                                const periodsDataMapped = PeriodResultMapper.fromPeriodResultContract(result,
                                        this.bundlesConfigService.timezone);
                                this.storeState({periodsData: periodsDataMapped});
                                this.periodsViewsService.updateViewsMappers(periodsDataMapped.periodsPage);
                            }
                        }));
    }

    private resetGlobalSearchCriteriaActions(): void {
        this.storeState({globalSearchAppliedCriteria: []});
        this.periodsViewsService.setGlobalSearchCriteriaStatus(false);
        this.resetGlobalSearchCriteria.emit();
    }

    private getCustomerBaseSelectTabsBundleType(bundlingCustomer: CustomerBaseContract[]): CustomerBundleTypeBaseContract {
        const customerCode = bundlingCustomer?.find((customer: CustomerBaseContract) => customer.code
                === this.selectedTabsService.customerCode);
        return customerCode?.customerBundleTypes.find((bundleType: CustomerBundleTypeBaseContract) =>
                bundleType.bundleType.code === this.selectedTabsService.bundleType);
    }

    private mapBundlesGlobalSearchCriteriaConfiguration(): void {
        this.state.bundlesGlobalDynamicSearchConfiguration = fastDeepCopy(this.state.initialBundlesGlobalSearchCriteriaConfiguration);
    }

    private mapBundleCriteriaSearchCriteria(bundlingCustomer: CustomerBaseContract[]): SearchCriteriaConfiguration[] {
        const bundleCriteriaSpecValue = this.getCustomerBaseSelectTabsBundleType(bundlingCustomer)?.bundleCriteriaSpec;
        const criteriaBundles: SearchCriteriaConfiguration[] = [];

        if (bundleCriteriaSpecValue != null && bundleCriteriaSpecValue?.length > 0) {
            bundleCriteriaSpecValue.map((bundleCriteriaSpec) => {
                const value: SearchCriteriaConfiguration = {
                    code: bundleCriteriaSpec.code,
                    name: bundleCriteriaSpec.name,
                    inputTypeSearch: SearchCriteriaInputTypeEnum.INPUT_TEXT_STRING,
                    dynamicCriteria: BundleDynamicCriteriaEnum.BUNDLE_CRITERIA_SPEC
                };
                criteriaBundles.push(value);
            });
        }
        return criteriaBundles;
    }

    private mapBundleCriteriaPositionSearchCriteria(bundlingCustomer: CustomerBaseContract[]): SearchCriteriaConfiguration[] {
        const bundleCriteriaSpecValue = this.getCustomerBaseSelectTabsBundleType(bundlingCustomer)?.bundlePositionCriteriaSpec;
        const criteriaBundles: SearchCriteriaConfiguration[] = [];

        if (bundleCriteriaSpecValue != null && bundleCriteriaSpecValue?.length > 0) {
            bundleCriteriaSpecValue.map((bundleCriteriaSpec) => {
                const value: SearchCriteriaConfiguration = {
                    code: bundleCriteriaSpec.code,
                    name: bundleCriteriaSpec.name,
                    inputTypeSearch: SearchCriteriaInputTypeEnum.INPUT_TEXT_STRING,
                    dynamicCriteria: BundleDynamicCriteriaEnum.BUNDLE_POSITION_CRITERIA_SPEC
                };
                criteriaBundles?.push(value);
            });
        }
        return criteriaBundles;
    }

    private resetCustomerBundleTypesSearchCriteria(): void {
        this.state.bundlesGlobalDynamicSearchConfiguration = null;
    }

    private mapCustomerBundleTypesSearchCriteria(bundling: CustomerBaseContract[]): void {

        this.mapBundlesGlobalSearchCriteriaConfiguration();
        const bundleCriteriaSearchCriteria = this.mapBundleCriteriaSearchCriteria(bundling);
        const bundleCriteriaPositionSearchCriteria = this.mapBundleCriteriaPositionSearchCriteria(bundling);
        const criteria = this.state.bundlesGlobalDynamicSearchConfiguration?.criteriaContexts
                .find((item) => item.code === 'bundles').criteria;
        criteria?.push(...bundleCriteriaSearchCriteria);
        criteria?.push(...bundleCriteriaPositionSearchCriteria);
    }

    private redirectToSelectTab(queryChips: QueryChipModel[]): void {
        const settlementsContext = queryChips.find((item) => item.filter.criterionContext === CriterionContextCodeEnum.SETTLEMENTS);
        const bundlesContext = queryChips.find((item) => item.filter.criterionContext === CriterionContextCodeEnum.BUNDLES);
        const periodContext = queryChips.find((item) => item.filter.criterionContext === CriterionContextCodeEnum.PERIODS);

        if (settlementsContext != null) {
            this.changePeriodViewTab(PeriodViewTabsEnum.SETTLEMENTS);
        } else if (bundlesContext != null) {
            this.changePeriodViewTab(PeriodViewTabsEnum.BUNDLES);
        } else if (periodContext != null) {
            this.changePeriodViewTab(PeriodViewTabsEnum.PERIODS);
        }
    }

    private getGlobalSearchCriteriaModel(): GlobalSearchCriteriaModel {
        return {
            autoCompleteDataResolverByCriterionCode: new Map([
                [
                    CriterionCodeEnum.SUPPLIER, (item: string, firstResult: number, totalResults: number):
                        Observable<ReferenceDataModel[]> => this.bundleService.getAllSuppliers(item, firstResult, totalResults)
                ]
            ]),
            dropdownDataResolverByCriterionCode: new Map([
                [
                    CriterionCodeEnum.PERIOD_TYPE, (): Observable<ReferenceDataModel[]> => this.bundleService.getAllPeriodTypes()
                ],
                [
                    CriterionCodeEnum.BUNDLE_STATUS, (): Observable<ReferenceDataModel[]> => this.bundleService.getAllBundleStatuses()
                ]
            ])
        };
    }

    private autoCloseBundlesPermissions(): void {
        if (this.userService.hasPermissions(
                [Permission.ADMIN_BUNDLES_AUTOCLOSEBUNDLES_VIEW])) {
            this.storeState({
                showAutoCloseBundles: true,
                autoCloseBundlesSwitchButton: {
                    checked: false,
                    disabled: true
                }
            });
        }
        if (this.userService.hasPermissions(
                [Permission.ADMIN_BUNDLES_AUTOCLOSEBUNDLES_MANAGE])) {
            this.storeState({
                showAutoCloseBundles: true,
                autoCloseBundlesSwitchButton: {
                    checked: false,
                    disabled: false
                }
            });
        }
    }

    private retrieveAutomaticBundlesClosureConfig(): void {
        if (this.state.showAutoCloseBundles) {
            this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
            this.configProperty.getBooleanValue(environment.bundlesBaseUrl.toString(),
                    `${this.selectedTabsService.customerCode}.${this.selectedTabsService.bundleType}.${this.autoCloseBundles}`)
                    .pipe(finalize(() => {
                        this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                    }))
                    .subscribe(value => {
                        this.storeState({
                            autoCloseBundlesSwitchButton: {
                                ...this.state.autoCloseBundlesSwitchButton,
                                checked: value?.value
                            },
                            autoCloseBundlesLastChangedOn: value?.lastChangedOn,
                            autoCloseBundlesLastChangedBy: value?.lastChangedBy
                        });
                    }, () => {
                        this.storeState({
                            autoCloseBundlesSwitchButton: {...this.state.autoCloseBundlesSwitchButton, checked: false},
                            autoCloseBundlesLastChangedBy: null,
                            autoCloseBundlesLastChangedOn: null
                        });
                    });
        }
    }

    private setAutomaticBundlesClosure(autoClose: boolean): void {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        this.subscriptions.add(
                this.configProperty.setValue(
                        environment.bundlesBaseUrl.toString(),
                        `${this.selectedTabsService.customerCode}.${this.selectedTabsService.bundleType}.${this.autoCloseBundles}`,
                        autoClose.toString())
                        .subscribe(() => {
                            if (autoClose) {
                                this.toast.success(`The automatic bundles closure process at the end of the period was successfully `
                                        + `activated for ${this.selectedTabsService.bundleTypeAcronym} `
                                        + `on ${this.selectedTabsService.customerAcronym}.`, 'Success!');
                            } else {
                                this.toast.success(`The automatic bundles closure process at the end of the period was successfully `
                                        + `inactivated for ${this.selectedTabsService.bundleTypeAcronym} `
                                        + `on ${this.selectedTabsService.customerAcronym}.<br>`
                                        + `<strong>Notice that this means that a bundles `
                                        + `manager must manually close the open bundles.</strong>`,
                                        'Success!');
                            }
                            this.getAutomaticBundlesClosure();
                        }, () => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                        }));
    }

    private getAutomaticBundlesClosure(): void {
        this.subscriptions.add(
                this.configProperty.getBooleanValue(
                        environment.bundlesBaseUrl.toString(),
                        `${this.selectedTabsService.customerCode}.${this.selectedTabsService.bundleType}.${this.autoCloseBundles}`)
                        .pipe(finalize(() => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                        }))
                        .subscribe(configProperty => {
                            this.storeState({
                                autoCloseBundlesLastChangedOn: configProperty?.lastChangedOn,
                                autoCloseBundlesLastChangedBy: configProperty?.lastChangedBy
                            });
                        }));
    }
}
